import React, { useEffect } from "react";
import ReactPlayer, { ReactPlayerProps } from "react-player/lazy";
import { useIsMounted } from "repoV2/utils/common/render/mount";
import { classNames } from "repoV2/utils/common/render/classNames";
import {
    getLoomVideoEmbedUrlFromShareUrl,
    isLoomVideoUrl,
} from "repoV2/features/Common/modules/URLsNRouting/URLsNRouting.utils";
import { appendURLSearchParams } from "repoV2/utils/urls&routing/urlParams";
import { checkIsFunction } from "@my-scoot/exly-react-component-lib";
import classes from "./VideoPlayer.module.scss";
import {
    DISABLE_KEYBOARD_YOUTUBE,
    ENABLE_KEYBOARD_YOUTUBE,
    REACT_PLAYER_DEFAULT_VOLUME,
} from "./VideoPlayer.constants";

export interface IVideoPlayerProps extends ReactPlayerProps {
    url: string | undefined;
    controls?: boolean;
    playIcon?:
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | undefined;
    style?: React.CSSProperties;
    className?: string;
    parentRef: React.RefObject<any>;
    autoPlay?: boolean;
    volume?: number;
    isMuted?: boolean;
    loop?: boolean;
    fitParent?: boolean;
    light?: boolean | string | undefined;
    playsinline?: boolean;
}

const VideoPlayer = ({
    url,
    controls,
    playIcon,
    style,
    className,
    parentRef,
    autoPlay,
    volume,
    isMuted,
    loop,
    light,
    fitParent,
    playsinline,
    ...restProps
}: IVideoPlayerProps) => {
    const isMounted = useIsMounted();
    const videoRef = React.useRef<any>(null);

    useEffect(() => {
        if (isMounted && autoPlay && checkIsFunction(videoRef?.current?.play)) {
            videoRef.current.play().catch((error: any) => {
                console.error("Error attempting to play", error);
            });
        }
    }, []);

    if (!isMounted) return null;

    if (url && isLoomVideoUrl(url)) {
        return (
            <div className={classes.loomIframeRoot}>
                <iframe
                    title={url}
                    width={fitParent ? parentRef.current.clientWidth : "100%"}
                    height={fitParent ? parentRef.current.clientHeight : "100%"}
                    className={classes.loomIframe}
                    src={appendURLSearchParams(
                        getLoomVideoEmbedUrlFromShareUrl(url),
                        { autoplay: autoPlay ? 1 : undefined }
                    )}
                    allowFullScreen
                />
            </div>
        );
    }

    if (url && !ReactPlayer.canPlay(url)) return null;

    return (
        parentRef &&
        parentRef.current && (
            <ReactPlayer
                url={url}
                ref={videoRef}
                playing={autoPlay}
                playsinline={playsinline}
                controls={controls}
                playIcon={playIcon}
                style={style}
                width={fitParent ? parentRef.current.clientWidth : "100%"}
                height={fitParent ? parentRef.current.clientHeight : "100%"}
                className={classNames(
                    classes.videoPlayerCommonStyle,
                    className
                )}
                // if the autoplay is on then only we want to set the volume other leave it to default volume (1)
                volume={
                    volume && autoPlay ? volume : REACT_PLAYER_DEFAULT_VOLUME
                } // muted in react player only works when volume={0} is given , reference link : https://www.npmjs.com/package/react-player
                // if the autoplay is on then only we want to set the mute to true, otherwise explicitly mark it as false
                muted={(isMuted && autoPlay) ?? false} // need to pass this false explicitly otherwise if isMuted or autoPlay is undefined (isMuted && autoPlay is evaluated to undefined), & browser overrides mute with true for some cases over undefined value.
                loop={loop}
                light={light}
                keyboard={controls} // if controls are hidden, keyoard should also get disabled
                config={{
                    facebook: {
                        // @ts-ignore:next-line
                        // Its working ignored the type issues thrown by TS
                        // Done for changing the Facebook player width and height according
                        // to parent container as it depends on following attributes
                        attributes: {
                            "data-width": `${
                                parentRef && parentRef.current
                                    ? parentRef.current.clientWidth
                                    : "400"
                            }px`,
                            "data-height": `${
                                parentRef && parentRef.current
                                    ? parentRef.current.clientHeight
                                    : "320"
                            }px`,
                        },
                    },
                    youtube: {
                        playerVars: {
                            // disablekb is used to toggle keyboard controls
                            disablekb: controls
                                ? DISABLE_KEYBOARD_YOUTUBE
                                : ENABLE_KEYBOARD_YOUTUBE,
                        },
                    }, // overriding youtube controls : https://www.npmjs.com/package/react-player
                    vimeo: {
                        playerOptions: {
                            colors: ["000000", "00ADEF", "FFFFFF", "000000"],
                            keyboard: controls,
                        },
                    },
                }}
                {...restProps}
            />
        )
    );
};

VideoPlayer.defaultProps = {
    controls: false,
    playIcon: undefined,
    style: {},
    className: "",
    autoPlay: false,
    volume: 1,
    isMuted: false,
    loop: false,
    light: false,
    fitParent: false,
    playsinline: true,
};

export default VideoPlayer;
